import React from 'react'
import { Loader2, ScanLine, Download, ChevronDown, ChevronRight } from 'lucide-react'
import type { SlaScanResult, SlaInitiativeResult } from '@/api/types'

const SOURCE_BADGE: Record<string, { label: string; color: string }> = {
  timespent: { label: 'Logged time', color: '#10b981' },
  none:      { label: 'No time',     color: '#6b7280' },
}

const HOURS_PER_DAY = 7.6

function fmtH(h: number) {
  if (h === 0) return '—'
  const d = h / HOURS_PER_DAY
  return d % 1 === 0 ? `${d}d` : `${d.toFixed(1)}d`
}

function InitiativeRow({ initiative }: InitiativeRowProps) {
  const [open, setOpen] = React.useState(false)
  const hasTime = initiative.ticketsWithTime > 0
  const sourceCounts = initiative.tickets.reduce<Record<string, number>>((acc, t) => {
    acc[t.parseSource] = (acc[t.parseSource] || 0) + 1
    return acc
  }, {})

  return (
    <>
      <tr
        className="border-t border-[var(--bg-primary)] cursor-pointer hover:bg-[var(--bg-primary)]/40 transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <td className="px-3 py-2 whitespace-nowrap">
          <span className="flex items-center gap-1.5">
            {open ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
            <span className="font-semibold text-primary text-[12px]">{initiative.key}</span>
          </span>
        </td>
        <td className="px-3 py-2 text-[var(--text-primary)] text-[12px] max-w-[240px] truncate">{initiative.summary}</td>
        <td className="px-3 py-2 text-center text-[12px] text-[var(--text-muted)]">{initiative.ticketCount}</td>
        <td className="px-3 py-2 text-center text-[12px]">
          <span
            className="font-semibold"
            style={{ color: hasTime ? '#6366f1' : 'var(--text-muted)', opacity: hasTime ? 1 : 0.5 }}
          >
            {fmtH(Math.round(initiative.totalHours * 10) / 10)}
          </span>
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1 flex-wrap">
            {Object.entries(sourceCounts).map(([src, count]) => {
              const cfg = SOURCE_BADGE[src] ?? SOURCE_BADGE.none
              return (
                <span
                  key={src}
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                  style={{ background: cfg.color + '20', color: cfg.color, border: `1px solid ${cfg.color}30` }}
                >
                  {count} {cfg.label}
                </span>
              )
            })}
          </div>
        </td>
      </tr>
      {open && initiative.tickets.map((ticket) => {
        const cfg = SOURCE_BADGE[ticket.parseSource] ?? SOURCE_BADGE.none
        return (
          <tr key={ticket.key} className="border-t border-[var(--bg-primary)]/50 bg-[var(--bg-primary)]/20">
            <td className="pl-8 pr-3 py-1.5" />
            <td className="px-3 py-1.5 text-[11px] text-[var(--text-muted)] max-w-[240px]">
              <a
                href={`https://frankieone.atlassian.net/browse/${ticket.key}`}
                target="_blank"
                rel="noreferrer"
                className="text-[var(--text-muted)] hover:text-primary no-underline hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {ticket.key}
              </a>
              <span className="ml-1.5 truncate">{ticket.summary}</span>
            </td>
            <td className="px-3 py-1.5 text-center text-[11px] text-[var(--text-muted)]" />
            <td className="px-3 py-1.5 text-center text-[11px] font-semibold" style={{ color: ticket.parsedHours > 0 ? cfg.color : 'var(--text-muted)' }}>
              {fmtH(ticket.parsedHours)}
            </td>
            <td className="px-3 py-1.5">
              <div className="flex items-center gap-2">
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                  style={{ background: cfg.color + '18', color: cfg.color }}
                >
                  {cfg.label}
                </span>
                {ticket.status && (
                  <span className="text-[10px] text-[var(--text-muted)]/60 truncate max-w-[160px]">
                    {ticket.status}
                  </span>
                )}
              </div>
            </td>
          </tr>
        )
      })}
    </>
  )
}

interface InitiativeRowProps {
  initiative: SlaInitiativeResult
}

interface Props {
  scanData: SlaScanResult | null
  scanning: boolean
  importing: boolean
  importResult: { initiatives: number; entries: number; totalHours: number } | null
  onScan: () => void
  onImport: () => void
  onClose: () => void
}

export function SlaImportModal({ scanData, scanning, importing, importResult, onScan, onImport, onClose }: Props) {
  const totalHours = scanData ? Math.round(scanData.initiatives.reduce((s, i) => s + i.totalHours, 0) * 10) / 10 : 0
  const initiativesWithTime = scanData ? scanData.initiatives.filter((i) => i.ticketsWithTime > 0).length : 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-2xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col overflow-hidden"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[var(--border-color)] flex items-center justify-between shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <ScanLine size={16} className="text-[#6366f1]" />
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">Import Jira SLA Time</h2>
            </div>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
              Sums logged time (timespent) from tickets under each initiative's Epics, plus any ticket carrying a <code className="text-[#6366f1]">time-tracking</code> label — excludes Cancelled and Rejected tickets.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] bg-transparent border-none cursor-pointer text-lg leading-none p-1"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!scanData && !scanning && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ScanLine size={32} className="text-[var(--text-muted)]/40 mb-3" />
              <p className="text-[var(--text-primary)] font-medium mb-1">Scan Jira for SLA time</p>
              <p className="text-[12px] text-[var(--text-muted)] max-w-sm mb-4">
                Finds tickets under each initiative's Epics plus any ticket carrying a <code className="text-[#6366f1]">time-tracking</code> label, walks up to its initiative, and sums the logged time. Cancelled and Rejected tickets are excluded.
              </p>
              <button
                onClick={onScan}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366f1] text-white text-[13px] font-semibold cursor-pointer border-none hover:opacity-90 transition-opacity"
              >
                <ScanLine size={14} /> Run scan
              </button>
            </div>
          )}

          {scanning && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Loader2 size={28} className="text-[#6366f1] animate-spin mb-3" />
              <p className="text-[var(--text-primary)] font-medium">Scanning Jira…</p>
              <p className="text-[11px] text-[var(--text-muted)] mt-1">Fetching initiatives, EPICs, and child tickets</p>
            </div>
          )}

          {!scanning && scanData && (
            <>
              {/* Summary stats */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Initiatives', value: scanData.scanned.initiatives },
                  { label: 'EPICs', value: scanData.scanned.epics },
                  { label: 'Tickets', value: scanData.scanned.tickets },
                  { label: 'With time', value: scanData.scanned.ticketsWithTime },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-[var(--bg-primary)] rounded-lg p-3 text-center">
                    <div className="text-[22px] font-bold text-[var(--text-primary)]">{value}</div>
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{label}</div>
                  </div>
                ))}
              </div>

              {/* Breakdown table */}
              <div className="rounded-lg border border-[var(--border-color)] overflow-hidden">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-[var(--bg-primary)]">
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Initiative</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Summary</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Tickets</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Hours</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanData.initiatives.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-[var(--text-muted)] text-[12px]">
                          No tickets found under any initiative. Check Jira hierarchy structure.
                        </td>
                      </tr>
                    ) : (
                      scanData.initiatives.map((init) => (
                        <InitiativeRow key={init.key} initiative={init} />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {importResult && (
            <div className="mt-4 rounded-lg p-3 text-sm bg-green-500/10 border border-green-500/20 text-green-400">
              ✓ Imported <strong>{importResult.entries}</strong> tickets across <strong>{importResult.initiatives}</strong> initiatives — <strong>{fmtH(importResult.totalHours)}</strong> total
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[var(--border-color)] flex items-center justify-between shrink-0">
          <div className="text-[11px] text-[var(--text-muted)]">
            {scanData && !importing && !importResult && (
              <span>Found <strong className="text-[var(--text-primary)]">{fmtH(totalHours)}</strong> across <strong className="text-[var(--text-primary)]">{initiativesWithTime}</strong> initiatives</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {scanData && !importResult && (
              <button
                onClick={onScan}
                disabled={scanning || importing}
                className="text-[11px] px-2.5 py-1 rounded-md border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer bg-transparent transition-colors disabled:opacity-40"
              >
                Re-scan
              </button>
            )}
            <button
              onClick={onClose}
              className="text-[11px] px-2.5 py-1 rounded-md border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer bg-transparent transition-colors"
            >
              {importResult ? 'Close' : 'Cancel'}
            </button>
            {scanData && !importResult && (
              <button
                onClick={onImport}
                disabled={importing || initiativesWithTime === 0}
                className="flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-md bg-[#6366f1] text-white font-semibold cursor-pointer border-none hover:opacity-90 transition-opacity disabled:opacity-40"
              >
                {importing
                  ? <><Loader2 size={11} className="animate-spin" /> Importing…</>
                  : <><Download size={11} /> Import {fmtH(totalHours)}</>
                }
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
